import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Dialog } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import InputField from 'shared/ui/InputForm';
import { callAPI } from 'shared/api/callApi';
import { MESSAGES, showSnackbar, isDuplicateName } from '../../../lib/configUtils';
import ItemDetailForm from './ItemDetailForm';

const initialFormData = {
    id: randomId(),
    il_trade_partner: null,
    il_duty_item_category: null,
    item_name: '',
    item_code: '',
    description: '',
    // 새 품목은 Tier 무관으로 시작한다 — 소주처럼 Tier를 고르는 품목만 담당자가 켠다.
    tier_required: false,
    il_duty_item_formula_mapping: [],
    il_duty_item_meta: [],
};

const initValidator = {
    il_trade_partner: false,
    il_duty_item_category: false,
    item_name: false,
    item_code: false,
};

export default function ItemAdd({
    newOpen,
    setNewOpen,
    itemRows,
    setItemRows,
    loadingState,
    itemFormulasRows,
    userEmail,
    getAccessTokenSilently,
    setLoadingState,
}) {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState(initialFormData);
    /** State for validating and displaying errors for required inputs. */
    const [validator, setValidator] = useState(initValidator);

    /** Check if form has any input values */
    const hasFormData = useCallback(() => {
        return (
            formData.item_code.trim() !== '' ||
            formData.il_trade_partner !== null ||
            formData.il_duty_item_category !== null ||
            formData.item_name.trim() !== '' ||
            formData.description.trim() !== '' ||
            formData.tier_required !== false ||
            formData.il_duty_item_formula_mapping.length > 0 ||
            formData.il_duty_item_meta.length > 0
        );
    }, [formData]);

    /** Handle dialog close with confirmation if form has data */
    const handleClose = useCallback(() => {
        if (hasFormData()) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
            if (!confirmed) {
                return;
            }
        }
        setFormData(initialFormData);
        setNewOpen(false);
    }, [hasFormData, setNewOpen]);

    const handleSave = () => {
        if (
            formData.item_code.trim() === '' ||
            formData.il_trade_partner === null ||
            formData.il_duty_item_category === null ||
            formData.item_name.trim() === ''
        ) {
            showSnackbar(dispatch, MESSAGES.EMPTY_ITEM, 'warning');
            return;
        }
        if (isDuplicateName(itemRows, formData.item_code, 'item_code')) {
            showSnackbar(dispatch, MESSAGES.DUPLICATE_ITEMS, 'warning');
            return;
        }
        const formattedFormData = {
            ...formData,
            // formula mapping → convert to formula_id array
            il_duty_item_formula_mapping: (formData.il_duty_item_formula_mapping || [])
                .map((m) => m.il_duty_formula_id)
                .filter(Boolean), // remove null, undefined

            // meta → only needed keys
            il_duty_item_meta: (formData.il_duty_item_meta || []).map((m) => ({
                meta_key: m.meta_key,
                meta_value: m.meta_value,
            })),
        };

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'duty_item',
            getAccessTokenSilently,
            email: userEmail,
            obj: formattedFormData,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: true }));
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: false }));
            },
            onSuccess: (resp) => {
                console.log('add item response', resp);
                dispatch(setSnackbar({ open: true, message: MESSAGES.ADD_SUCCESS, severity: 'success' }));
                setItemRows([...itemRows, resp.data]);
                setFormData(initialFormData);
                setNewOpen(false);
            },
            onError: (error) => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: error.message,
                        severity: 'error',
                    }),
                );
            },
        });
    };

    return (
        <Dialog
            maxWidth="xl"
            fullWidth={true}
            open={newOpen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                },
            }}
            PaperComponent={PaperComponent}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '500px' }}>
                <DialogHeader cursor title="Add Item" onClose={handleClose} onSave={handleSave} />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        height: '100%',
                        overflowY: 'auto',
                    }}
                >
                    <ItemDetailForm
                        itemDetail={formData}
                        setItemDetail={setFormData}
                        loadingState={loadingState}
                        itemFormulasRows={itemFormulasRows}
                        validator={validator}
                        setValidator={setValidator}
                    />
                </Box>
            </Box>
        </Dialog>
    );
}
